import type { IGenericAPIResponse } from '#api';
import type { IAffiliateGlobalConfig, IAlertData, IGlobalConfig } from '#shared/types/firebase.types';
import { stores } from '~/store/index';

const DB_PATH_ALERT_SYSTEM = 'ALERT_SYSTEM';
const DB_PATH_CHALLENGE_SYSTEM = 'CHALLENGE_SYSTEM';
const DB_PATH_AFFILIATE_SYSTEM = 'affiliate_system';
const DB_PATH_CONFIG_GLOBAL = 'config/global';
const DB_PATH_TOKEN_PURCHASES = 'TOKENPURCHASES';

const MS_ONE_MONTH = 60 * 60 * 24 * 31 * 1000;

export const EAlertTabTypes = {
  All: 'all',
  Rewards: 'rewards',
  Requests: 'requests',
} as const;
export type TAlertTabTypes = (typeof EAlertTabTypes)[keyof typeof EAlertTabTypes];

function showBrowserNotification(title: string, body: string, icon: string) {
  if (window.Notification && Notification.permission === 'granted') {
    const notification = new Notification(title, {
      body,
      icon,
      silent: true,
    });
    notification.onclick = () => {
      window.focus();
    };
  }
}

export const useFirebaseStore = defineStore('firebase', () => {
  const gtag = useGtag();

  // MARK: Friend System
  const lastPlayedUsers = ref([]);

  function removeFromListOfLastPlayedWith(payload: IFriendData) {
    for (const item in lastPlayedUsers.value) {
      const index = Number.parseInt(item, 10);
      if (lastPlayedUsers.value[item]?.displayName === payload.displayName) {
        lastPlayedUsers.value.splice(index, 1);
      }
    }
  }

  function appendLastPlayedWith(payload: IFriendData) {
    let addNew = true;

    for (const item in lastPlayedUsers.value) {
      if (lastPlayedUsers.value[item]?.displayName === payload.displayName) {
        lastPlayedUsers.value[item] = payload;
        addNew = false;
      }
    }

    if (addNew) {
      lastPlayedUsers.value.push(payload);
    }
  }

  async function setupLastPlayedUsersListeners() {
    const { $firebase, $socket } = useTypedNuxtApp();
    const userKey = stores.user.getUserId();

    if (!userKey) return;

    $firebase.database
      .ref('OPPONENT_SYSTEM')
      .child(userKey)
      .orderByKey()
      .limitToLast(10)
      .on('child_added', (data) => {
        const lastPlayed = data.val();
        lastPlayed.online = false;
        $socket.emit('subscribeToOnlineStatus', { onlineUsers: [lastPlayed.displayName] });
        appendLastPlayedWith(lastPlayed);
      });

    $firebase.database
      .ref('OPPONENT_SYSTEM')
      .child(userKey)
      .orderByKey()
      .limitToLast(10)
      .on('child_changed', (data) => {
        const lastPlayed = data.val();
        lastPlayed.online = false;
        $socket.emit('subscribeToOnlineStatus', { onlineUsers: [lastPlayed.displayName] });
        appendLastPlayedWith(lastPlayed);
      });

    $firebase.database
      .ref('OPPONENT_SYSTEM')
      .child(userKey)
      .orderByKey()
      .limitToLast(10)
      .on('child_removed', (data) => {
        const lastPlayed = data.val();
        removeFromListOfLastPlayedWith(lastPlayed);
      });
  }

  // MARK: Alert System
  const alerts = ref<IAlertData[]>([]);

  const filteredAlerts = computed(() =>
    alerts.value.filter((a) => !a.deleted && !a.message?.toLowerCase().includes('daily bonus')),
  );

  function subscribeToFriendOnlineStatus(displayName: string) {
    const { $socket } = useTypedNuxtApp();
    $socket.emit('unsubscribeToOnlineStatus', { displayNames: [displayName] });
    $socket.emit('subscribeToOnlineStatus', { onlineUsers: [displayName] });
  }

  async function processAlert(alertKey: string, data: IAlertData) {
    const userKey = stores.user.getUserId();
    if (!userKey) return;

    const { $firebase } = useTypedNuxtApp();
    const isOlderThanOneMonth = data.timestamp < Date.now() - MS_ONE_MONTH;

    data.recordKey = alertKey;

    if (isOlderThanOneMonth) {
      await $firebase.database?.ref(DB_PATH_ALERT_SYSTEM).child(userKey).child(alertKey).remove();
      return;
    }

    if (data.type === EAlertType.OpenCashier) {
      gtag.reportGtagConversion();
      stores.modals.showCashierModal();
      await $firebase.database?.ref(DB_PATH_ALERT_SYSTEM).child(userKey).child(alertKey).remove();
      return;
    }

    const filterRequest: TAlertType[] = [EAlertType.FriendRequest, EAlertType.Request];
    if (filterRequest.includes(data.type) && data.displayName) {
      subscribeToFriendOnlineStatus(data.displayName);
    }

    const idx = alerts.value.findIndex((a) => a.recordKey === alertKey);
    if (idx === -1) {
      alerts.value.push(data);
    } else {
      alerts.value[idx] = data;
    }
  }

  function setupAlertSystemListeners() {
    const userKey = stores.user.getUserId();
    if (!userKey) return;

    const fetchAlerts = async () => {
      const userKey = stores.user.getUserId();
      if (!userKey) return;

      const { $firebase } = useTypedNuxtApp();

      const snapshot = await $firebase.database?.ref(DB_PATH_ALERT_SYSTEM).child(userKey).once('value');
      const alerts: Record<string, IAlertData> = snapshot.val();
      if (!alerts) return;

      await Promise.all(Object.entries(alerts).map(([alertKey, value]) => processAlert(alertKey, value)));
    };

    const setupAlertListeners = async () => {
      const userKey = stores.user.getUserId();
      if (!userKey) return;

      const { $firebase } = useTypedNuxtApp();
      const date = Date.now();

      const userTableRef = $firebase.database?.ref(DB_PATH_ALERT_SYSTEM).child(userKey);

      const onAlert = async (snapshot: any, setAsNew: boolean) => {
        if (!snapshot.key) return;

        const data = snapshot.val();
        await processAlert(snapshot.key, data);

        if (setAsNew) {
          showBrowserNotification('New notification received', data.message, '/favicon.ico');
        }
      };

      userTableRef.off('child_added');
      userTableRef.off('child_changed');

      userTableRef
        .orderByChild('timestamp')
        .startAt(date)
        .on('child_added', (snapshot: any) => onAlert(snapshot, true));

      userTableRef.on('child_changed', (snapshot: any) => onAlert(snapshot, false));
    };

    fetchAlerts();
    setupAlertListeners();

    const handleVisibilityChange = () => {
      if (document.hidden) return;
      fetchAlerts();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange, false);
  }

  function stopAlertSystemListeners() {
    const { $firebase } = useTypedNuxtApp();
    $firebase.database?.ref(DB_PATH_ALERT_SYSTEM).off();
    alerts.value = [];
  }

  async function markAlertAsDeleted(alertKey: string) {
    const { $firebase } = useTypedNuxtApp();
    const userKey = stores.user.getUserId();
    if (!userKey) return;

    $firebase.database?.ref(DB_PATH_ALERT_SYSTEM).child(userKey).child(alertKey).update({ deleted: true });

    const alert = alerts.value.find((a) => a.recordKey === alertKey);
    if (alert) alert.deleted = true;
  }

  // MARK: Challenge System
  async function markChallengeAsDeleted(challengeKey: string) {
    const { $firebase } = useTypedNuxtApp();
    const userKey = stores.user.getUserId();
    if (!userKey) return;

    $firebase.database?.ref(DB_PATH_CHALLENGE_SYSTEM).child(userKey).child(challengeKey).update({ deleted: true });
  }

  async function markAllAlertsAsDeleted(type: TAlertTabTypes) {
    const userKey = stores.user.getUserId();
    if (!userKey) return;

    const { $firebase } = useTypedNuxtApp();

    const markAsDeleted = (snapshot: any, typeFilter: string) => {
      const updates: any = {};

      snapshot.forEach((notificationSnapshot: any) => {
        const notification = notificationSnapshot.val();
        const allType = typeFilter === 'all';
        const matchesFilterType = notification.type === typeFilter;
        const isRequestType =
          typeFilter === 'requests' && [EAlertType.Request, EAlertType.FriendRequest].includes(notification.type);

        if (allType || isRequestType || matchesFilterType) {
          updates[`${notificationSnapshot.key}/deleted`] = true;
        }
      });
      return updates;
    };

    const alertPromise = $firebase.database
      .ref(DB_PATH_ALERT_SYSTEM)
      .child(userKey)
      .once('value')
      .then((alertSnapshot) => {
        const updates = markAsDeleted(
          alertSnapshot,
          type === 'rewards' ? 'REWARD' : type === 'requests' ? 'requests' : 'all',
        );
        return $firebase.database.ref(DB_PATH_ALERT_SYSTEM).child(userKey).update(updates);
      });

    const challengePromise = $firebase.database
      .ref(DB_PATH_CHALLENGE_SYSTEM)
      .child(userKey)
      .once('value')
      .then((challengeSnapshot) => {
        const updates = markAsDeleted(
          challengeSnapshot,
          type === 'rewards' ? 'REWARD' : type === 'requests' ? 'requests' : 'all',
        );
        return $firebase.database.ref(DB_PATH_CHALLENGE_SYSTEM).child(userKey).update(updates);
      });

    Promise.all([alertPromise, challengePromise])
      .then(() => {})
      .catch((error) => {
        console.error('Error deleting notifications: ', error);
      });
  }

  // MARK: Affiliate System
  const affiliateGlobalConfig = ref<IAffiliateGlobalConfig>({
    affiliatesEarnPercentage: 0,
    minAmountTransfer: 0,
    minAmountWithdraw: 0,
  });

  async function loadAffiliateGlobalConfig() {
    const { $firebase } = useTypedNuxtApp();
    const snapshot = await $firebase.database?.ref(DB_PATH_AFFILIATE_SYSTEM).child('global_config').once('value');
    if (!snapshot?.val()) return;
    affiliateGlobalConfig.value = snapshot.val();
  }

  // MARK: Global config
  const globalConfig = ref<IGlobalConfig>();

  async function getGlobalConfig() {
    const { $firebase } = useTypedNuxtApp();
    const snapshot = await $firebase.database?.ref(DB_PATH_CONFIG_GLOBAL).once('value');
    if (!snapshot?.val()) return;
    globalConfig.value = snapshot.val();
  }

  // MARK: Referral Rewards
  async function getUserTotalReferralRewards() {
    const userKey = stores.user.getUserId();
    if (!userKey) return;

    const { $firebase } = useTypedNuxtApp();

    return $firebase.database
      ?.ref(`${DB_PATH_AFFILIATE_SYSTEM}/rewards/byaffiliate/${userKey}`)
      .once('value')
      .then((snapshot) => {
        if (!snapshot.exists()) {
          return 0;
        }

        const rewardsData: Record<string, any> = snapshot.val();

        return Object.values(rewardsData).reduce((total, reward) => {
          return (
            total + Object.values(reward).reduce((subTotal, entry: any) => subTotal + (entry.totalsignupMMC || 0), 0)
          );
        }, 0);
      })
      .catch((error) => {
        console.error(error);
      });
  }

  // MARK: Notifications actions
  async function acceptFriendRequest(friendToAcceptId: string) {
    const { $authApi } = useTypedNuxtApp();

    try {
      const sessionKey = await stores.user.getIdToken();
      return await $authApi.post<IGenericAPIResponse>('/approvefriend', {
        sessionKey,
        friendToAcceptId,
      });
    } catch (error: any) {
      console.error(error);
      return {
        status: 'ERROR',
        message: error.message || 'Failed to accept friend request',
      };
    }
  }

  async function rejectFriendRequest(friendToDeclineId: string) {
    const { $authApi } = useTypedNuxtApp();

    try {
      const sessionKey = await stores.user.getIdToken();
      return await $authApi.post<IGenericAPIResponse>('/rejectfriend', { sessionKey, friendToDeclineId });
    } catch (error: any) {
      console.error(error);
      return {
        status: 'ERROR',
        message: error.message || 'Failed to accept friend request',
      };
    }
  }

  // MARK: Promo packages
  const boughtPackages = ref<Map<string, IPurchaseData>>(new Map<string, IPurchaseData>());

  async function loadUserBoughtPackages() {
    const userKey = stores.user.getUserId();
    if (!userKey) return;

    const { $firebase } = useTypedNuxtApp();
    const snapshot = await $firebase.database?.ref(`${DB_PATH_TOKEN_PURCHASES}/${userKey}`).once('value');
    const value = snapshot.val() || {};
    boughtPackages.value = new Map(Object.entries(value));
  }

  function setInfoConnectedListener() {
    const connectedRef = useTypedNuxtApp().$firebase.database?.ref('.info/connected');

    connectedRef?.on('value', async (snapshot) => {
      if (snapshot.val() === false) return;

      const hasActiveUrl = !!stores.games.gameData?.url;
      useTypedNuxtApp().$socket.emit('playing', { isPlaying: hasActiveUrl });
    });
  }

  return {
    lastPlayedUsers,
    markAlertAsDeleted,
    markAllAlertsAsDeleted,

    boughtPackages,

    alerts,
    filteredAlerts,
    setupAlertSystemListeners,
    stopAlertSystemListeners,

    markChallengeAsDeleted,

    affiliateGlobalConfig,
    loadAffiliateGlobalConfig,

    globalConfig,
    getGlobalConfig,

    getUserTotalReferralRewards,
    loadUserBoughtPackages,

    acceptFriendRequest,
    rejectFriendRequest,
    setupLastPlayedUsersListeners,

    setInfoConnectedListener,
  };
});
